import React from 'react';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import {
  AskNovoAiQuestionResponse,
  AskNovoAiStreamBootstrapResponse,
  AskNovoAiStreamRequestPayload,
  changeDrawerState,
  closeNovoAIDialog,
  getHelpOptionsSettings,
  getRecentQuestions,
  openNovoAIDialog,
  resetAskNovoAiUiState,
  setAskNovoAiResponse,
  startAskNovoAiStreamSession,
} from 'redux/actions/help-options';
import { AskNovoAiDrawerState, AskNovoAiState } from './helpers';
import streamAskNovoAiResponse, {
  AskNovoAiStreamReference,
  AskNovoAiStreamResultPayload,
} from './stream-ask-novoai-response';

const STATUS_MIN_VISIBLE_MS = 450;
const STATUS_SEQUENCE_INTERVAL_MS = 1_600;

export type LecturePageTitleMap = Map<string, string>;

type UseAskNovoAiStreamParams = {
  catalogId: string;
  lecturePageTitleById: LecturePageTitleMap;
  isProcessingFilesInProgress?: boolean;
};

type SnakeCaseAskNovoAiStreamBootstrapResponse = {
  jwt_token?: AskNovoAiStreamBootstrapResponse['jwtToken'];
  expires_at?: AskNovoAiStreamBootstrapResponse['expiresAt'];
  stream_id?: AskNovoAiStreamBootstrapResponse['streamId'];
  ai_api_endpoint?: AskNovoAiStreamBootstrapResponse['aiApiEndpoint'];
  course_id?: AskNovoAiStreamBootstrapResponse['courseId'];
  user_id?: AskNovoAiStreamBootstrapResponse['userId'];
  lecture_page_ids?: AskNovoAiStreamBootstrapResponse['lecturePageIds'];
  file_ids?: AskNovoAiStreamBootstrapResponse['fileIds'];
  action_base_url?: AskNovoAiStreamBootstrapResponse['actionBaseUrl'];
  persistence_callback_url?: AskNovoAiStreamBootstrapResponse['persistenceCallbackUrl'];
  data_env?: AskNovoAiStreamBootstrapResponse['dataEnv'];
  response_language?: AskNovoAiStreamBootstrapResponse['responseLanguage'];
};

type NormalizedAskNovoAiStreamBootstrapResponse =
  AskNovoAiStreamBootstrapResponse & SnakeCaseAskNovoAiStreamBootstrapResponse;

const getStreamReferenceFallbackTitle = () => (
  t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.STREAM_REFERENCE_FALLBACK_TITLE()
);

const getStreamStatusSequence = () => ([
  t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.STREAM_STATUS_STEP_1(),
  t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.STREAM_STATUS_STEP_2(),
  t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.STREAM_STATUS_STEP_3(),
  t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.STREAM_STATUS_STEP_4(),
  t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.STREAM_STATUS_STEP_5(),
]);

type SharedAskNovoAiStreamState = {
  activeQuestion: string;
  streamStatusMessage: string;
  streamingAnswer: string;
};

type SharedAskNovoAiStreamRuntime = {
  hasStreamedChunk: boolean;
  lastVisibleStreamStatusAt: number;
  loadingStatusInterval: number | null;
  pendingStreamStatus: string | null;
  streamResponse: AskNovoAiQuestionResponse | null;
  streamSession: AskNovoAiStreamBootstrapResponse | null;
  streamSessionCatalogId: string | null;
  streamSessionRequest: Promise<AskNovoAiStreamBootstrapResponse> | null;
  streamStatusTimer: number | null;
  streamingAnswer: string;
};

const initialSharedAskNovoAiStreamState: SharedAskNovoAiStreamState = {
  activeQuestion: '',
  streamStatusMessage: '',
  streamingAnswer: '',
};

let sharedAskNovoAiStreamState: SharedAskNovoAiStreamState = {
  ...initialSharedAskNovoAiStreamState,
};

const sharedAskNovoAiStreamListeners = new Set<(state: SharedAskNovoAiStreamState) => void>();

let sharedActiveSubmissionId = 0;
let sharedAbortController: AbortController | null = null;
const sharedAskNovoAiStreamRuntime: SharedAskNovoAiStreamRuntime = {
  hasStreamedChunk: false,
  lastVisibleStreamStatusAt: 0,
  loadingStatusInterval: null,
  pendingStreamStatus: null,
  streamResponse: null,
  streamSession: null,
  streamSessionCatalogId: null,
  streamSessionRequest: null,
  streamStatusTimer: null,
  streamingAnswer: '',
};

const publishSharedAskNovoAiStreamState = () => {
  const snapshot = { ...sharedAskNovoAiStreamState };
  sharedAskNovoAiStreamListeners.forEach((listener) => listener(snapshot));
};

const setSharedAskNovoAiStreamState = (
  partialState: Partial<SharedAskNovoAiStreamState>,
) => {
  sharedAskNovoAiStreamState = {
    ...sharedAskNovoAiStreamState,
    ...partialState,
  };
  publishSharedAskNovoAiStreamState();
};

const resetSharedAskNovoAiStreamState = () => {
  sharedAskNovoAiStreamState = {
    ...initialSharedAskNovoAiStreamState,
  };
  publishSharedAskNovoAiStreamState();
};

const buildStreamExternalReferences = (references: AskNovoAiStreamReference[] = []) => {
  const seenUrls = new Set<string>();

  return references.reduce<Array<{ title: string; url: string }>>((accumulator, reference) => {
    if (!reference.url || seenUrls.has(reference.url) || reference.certificate_url || reference.component_id || reference.live_session_id) {
      return accumulator;
    }

    seenUrls.add(reference.url);
    accumulator.push({
      title: reference.title || reference.url,
      url: reference.url,
    });
    return accumulator;
  }, []);
};

const buildStreamLiveEvents = (
  liveEvents: AskNovoAiStreamResultPayload['liveEvents'] = [],
) => liveEvents.map((liveEvent) => ({
  id: liveEvent.id,
  lecturePageId: liveEvent.lecturePageId,
  title: liveEvent.title || '',
  startTime: liveEvent.startTime,
  endTime: liveEvent.endTime,
}));

const buildStreamReferencedComponents = (
  references: AskNovoAiStreamReference[] = [],
  lecturePageTitleById: LecturePageTitleMap = new Map<string, string>(),
) => {
  const seenKeys = new Set<string>();

  return references.reduce<Array<{
    id: number;
    type: string;
    index: number;
    lecturePage: {
      id: number;
      title: string;
    };
  }>>((accumulator, reference, index) => {
    const lecturePageId = Number(reference.lecture_page_id);
    if (!Number.isFinite(lecturePageId) || lecturePageId < 0) {
      return accumulator;
    }

    const componentId = Number(reference.component_id);
    const id = Number.isFinite(componentId) ? componentId : lecturePageId;
    const dedupeKey = `${lecturePageId}:${id}`;
    if (seenKeys.has(dedupeKey)) {
      return accumulator;
    }

    seenKeys.add(dedupeKey);
    accumulator.push({
      id,
      type: '',
      index,
      lecturePage: {
        id: lecturePageId,
        title: (
          lecturePageTitleById.get(String(lecturePageId))
          || reference.title
          || getStreamReferenceFallbackTitle()
        ),
      },
    });
    return accumulator;
  }, []);
};

const buildImmediateStreamResponse = (
  result: AskNovoAiStreamResultPayload,
  lecturePageTitleById: LecturePageTitleMap,
): AskNovoAiQuestionResponse => {
  const references = result.references || [];
  const certificateUrl = result.certificateUrl
    || references.find((reference) => reference.certificate_url)?.certificate_url;

  return {
    id: result.qnaId,
    questionText: result.questionText,
    answer: result.answer,
    certificateUrl,
    responseCode: result.responseCode,
    liveEvents: buildStreamLiveEvents(result.liveEvents),
    referencedComponents: buildStreamReferencedComponents(references, lecturePageTitleById),
    externalReferences: result.externalReferences || buildStreamExternalReferences(references),
  };
};

const isStreamSessionValid = (session?: AskNovoAiStreamBootstrapResponse | null) => {
  if (!session?.expiresAt) return false;

  const expiresAt = new Date(session.expiresAt).getTime();
  return Number.isFinite(expiresAt) && expiresAt - Date.now() > 30_000;
};

const normalizeStreamSessionPayload = (
  sessionPayload: AskNovoAiStreamBootstrapResponse,
): AskNovoAiStreamBootstrapResponse => {
  const normalizedSessionPayload = sessionPayload as NormalizedAskNovoAiStreamBootstrapResponse;

  return {
    ...sessionPayload,
    jwtToken: normalizedSessionPayload.jwtToken ?? normalizedSessionPayload.jwt_token,
    expiresAt: normalizedSessionPayload.expiresAt ?? normalizedSessionPayload.expires_at,
    streamId: normalizedSessionPayload.streamId ?? normalizedSessionPayload.stream_id,
    aiApiEndpoint: normalizedSessionPayload.aiApiEndpoint ?? normalizedSessionPayload.ai_api_endpoint,
    courseId: normalizedSessionPayload.courseId ?? normalizedSessionPayload.course_id,
    userId: normalizedSessionPayload.userId ?? normalizedSessionPayload.user_id,
    lecturePageIds: normalizedSessionPayload.lecturePageIds ?? normalizedSessionPayload.lecture_page_ids,
    fileIds: normalizedSessionPayload.fileIds ?? normalizedSessionPayload.file_ids,
    actionBaseUrl: normalizedSessionPayload.actionBaseUrl ?? normalizedSessionPayload.action_base_url,
    persistenceCallbackUrl: normalizedSessionPayload.persistenceCallbackUrl ?? normalizedSessionPayload.persistence_callback_url,
    dataEnv: normalizedSessionPayload.dataEnv ?? normalizedSessionPayload.data_env,
    responseLanguage: normalizedSessionPayload.responseLanguage ?? normalizedSessionPayload.response_language,
  } as AskNovoAiStreamBootstrapResponse;
};

const useAskNovoAiStream = ({
  catalogId,
  lecturePageTitleById,
  isProcessingFilesInProgress,
}: UseAskNovoAiStreamParams) => {
  const dispatch = useAppDispatch();
  const askNovoAi: AskNovoAiState = useSelector(
    (state) => state.app.askNovoAi,
  );
  const [sharedStreamState, setSharedStreamState] = React.useState<SharedAskNovoAiStreamState>(
    sharedAskNovoAiStreamState,
  );
  const { activeQuestion, streamStatusMessage, streamingAnswer } = sharedStreamState;

  const clearPendingStreamStatusTimer = React.useCallback(() => {
    if (sharedAskNovoAiStreamRuntime.streamStatusTimer !== null) {
      window.clearTimeout(sharedAskNovoAiStreamRuntime.streamStatusTimer);
      sharedAskNovoAiStreamRuntime.streamStatusTimer = null;
    }
  }, []);

  const clearLoadingStatusInterval = React.useCallback(() => {
    if (sharedAskNovoAiStreamRuntime.loadingStatusInterval !== null) {
      window.clearInterval(sharedAskNovoAiStreamRuntime.loadingStatusInterval);
      sharedAskNovoAiStreamRuntime.loadingStatusInterval = null;
    }
  }, []);

  React.useEffect(() => {
    const listener = (nextState: SharedAskNovoAiStreamState) => {
      setSharedStreamState(nextState);
    };

    sharedAskNovoAiStreamListeners.add(listener);
    listener(sharedAskNovoAiStreamState);

    return () => {
      sharedAskNovoAiStreamListeners.delete(listener);
    };
  }, []);

  const setVisibleStreamStatus = React.useCallback((message: string, force = false) => {
    if (!message) return;
    if (!force && message === sharedAskNovoAiStreamState.streamStatusMessage) return;

    const applyMessage = () => {
      setSharedAskNovoAiStreamState({ streamStatusMessage: message });
      sharedAskNovoAiStreamRuntime.lastVisibleStreamStatusAt = Date.now();
      sharedAskNovoAiStreamRuntime.pendingStreamStatus = null;
      sharedAskNovoAiStreamRuntime.streamStatusTimer = null;
    };

    const elapsedSinceLastStatus = Date.now() - sharedAskNovoAiStreamRuntime.lastVisibleStreamStatusAt;
    if (
      force
      || sharedAskNovoAiStreamRuntime.lastVisibleStreamStatusAt === 0
      || elapsedSinceLastStatus >= STATUS_MIN_VISIBLE_MS
    ) {
      clearPendingStreamStatusTimer();
      applyMessage();
      return;
    }

    sharedAskNovoAiStreamRuntime.pendingStreamStatus = message;
    if (sharedAskNovoAiStreamRuntime.streamStatusTimer !== null) return;

    sharedAskNovoAiStreamRuntime.streamStatusTimer = window.setTimeout(() => {
      const pendingMessage = sharedAskNovoAiStreamRuntime.pendingStreamStatus;
      if (pendingMessage) {
        setSharedAskNovoAiStreamState({ streamStatusMessage: pendingMessage });
        sharedAskNovoAiStreamRuntime.lastVisibleStreamStatusAt = Date.now();
      }
      sharedAskNovoAiStreamRuntime.pendingStreamStatus = null;
      sharedAskNovoAiStreamRuntime.streamStatusTimer = null;
    }, STATUS_MIN_VISIBLE_MS - elapsedSinceLastStatus);
  }, [clearPendingStreamStatusTimer]);

  const startLoadingStatusSequence = React.useCallback(() => {
    const streamStatusSequence = getStreamStatusSequence();

    clearLoadingStatusInterval();
    setVisibleStreamStatus(streamStatusSequence[0], true);

    let nextIndex = 1;
    if (streamStatusSequence.length < 2) return;

    sharedAskNovoAiStreamRuntime.loadingStatusInterval = window.setInterval(() => {
      const nextMessage = streamStatusSequence[nextIndex];
      if (!nextMessage) {
        clearLoadingStatusInterval();
        return;
      }

      setVisibleStreamStatus(nextMessage);
      if (nextIndex >= streamStatusSequence.length - 1) {
        clearLoadingStatusInterval();
        return;
      }

      nextIndex += 1;
    }, STATUS_SEQUENCE_INTERVAL_MS);
  }, [clearLoadingStatusInterval, setVisibleStreamStatus]);

  const resetLocalStreamState = React.useCallback(() => {
    clearPendingStreamStatusTimer();
    clearLoadingStatusInterval();
    sharedAskNovoAiStreamRuntime.pendingStreamStatus = null;
    sharedAskNovoAiStreamRuntime.lastVisibleStreamStatusAt = 0;
    sharedAskNovoAiStreamRuntime.hasStreamedChunk = false;
    sharedAskNovoAiStreamRuntime.streamResponse = null;
    sharedAskNovoAiStreamRuntime.streamingAnswer = '';
    resetSharedAskNovoAiStreamState();
  }, [clearLoadingStatusInterval, clearPendingStreamStatusTimer]);

  const invalidateActiveSubmission = React.useCallback(() => {
    sharedActiveSubmissionId += 1;
  }, []);

  const isActiveSubmission = React.useCallback(
    (submissionId: number) => sharedActiveSubmissionId === submissionId,
    [],
  );

  const prefetchStreamSession = React.useCallback(async (force = false) => {
    if (
      !force
      && sharedAskNovoAiStreamRuntime.streamSessionCatalogId === catalogId
      && isStreamSessionValid(sharedAskNovoAiStreamRuntime.streamSession)
    ) {
      return sharedAskNovoAiStreamRuntime.streamSession;
    }

    if (
      !force
      && sharedAskNovoAiStreamRuntime.streamSessionCatalogId === catalogId
      && sharedAskNovoAiStreamRuntime.streamSessionRequest
    ) {
      return sharedAskNovoAiStreamRuntime.streamSessionRequest;
    }

    const requestCatalogId = catalogId;
    sharedAskNovoAiStreamRuntime.streamSessionCatalogId = requestCatalogId;
    const sessionRequest = dispatch(startAskNovoAiStreamSession(catalogId))
      .unwrap()
      .then((sessionPayload) => {
        const normalizedSession = normalizeStreamSessionPayload(sessionPayload);

        if (
          sharedAskNovoAiStreamRuntime.streamSessionRequest === sessionRequest
          && sharedAskNovoAiStreamRuntime.streamSessionCatalogId === requestCatalogId
        ) {
          sharedAskNovoAiStreamRuntime.streamSession = normalizedSession;
        }
        return normalizedSession;
      })
      .finally(() => {
        if (sharedAskNovoAiStreamRuntime.streamSessionRequest === sessionRequest) {
          sharedAskNovoAiStreamRuntime.streamSessionRequest = null;
        }
      });

    sharedAskNovoAiStreamRuntime.streamSessionRequest = sessionRequest;
    return sessionRequest;
  }, [catalogId, dispatch]);

  const handleCloseDialog = React.useCallback(() => {
    invalidateActiveSubmission();
    sharedAbortController?.abort();
    sharedAbortController = null;
    if (askNovoAi.drawerState === AskNovoAiDrawerState.LOADING) {
      dispatch(changeDrawerState(AskNovoAiDrawerState.INITIAL));
      resetLocalStreamState();
    }
    dispatch(closeNovoAIDialog());
  }, [askNovoAi.drawerState, dispatch, invalidateActiveSubmission, resetLocalStreamState]);

  const onSubmit = React.useCallback(async (questionText: string) => {
    if (!questionText) return;
    const submissionId = sharedActiveSubmissionId + 1;
    sharedActiveSubmissionId = submissionId;

    dispatch(changeDrawerState(AskNovoAiDrawerState.LOADING));
    resetLocalStreamState();
    setSharedAskNovoAiStreamState({ activeQuestion: questionText });
    startLoadingStatusSequence();

    sharedAbortController?.abort();
    sharedAbortController = new AbortController();
    const streamAbortController = sharedAbortController;

    try {
      const sessionPayload = await prefetchStreamSession();
      if (!isActiveSubmission(submissionId)) return;
      const activeSession = sessionPayload;

      const { aiApiEndpoint } = activeSession;
      const { jwtToken } = activeSession;
      const {
        streamId,
        courseId,
        userId,
        lecturePageIds,
        fileIds,
        actionBaseUrl,
        persistenceCallbackUrl,
        dataEnv,
        responseLanguage,
      } = activeSession;

      if (!aiApiEndpoint || !jwtToken || !streamId || !courseId || !userId || !actionBaseUrl || !persistenceCallbackUrl || !dataEnv) {
        throw new Error(
          t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.NETWORK_ERROR_MESSAGE(),
        );
      }

      const streamRequestWithQuestion: AskNovoAiStreamRequestPayload = {
        streamId,
        courseId,
        userId,
        lecturePageIds,
        fileIds,
        actionBaseUrl,
        persistenceCallbackUrl,
        dataEnv,
        responseLanguage,
        userQuery: questionText,
      };
      sharedAskNovoAiStreamRuntime.streamSession = null;
      sharedAskNovoAiStreamRuntime.streamSessionCatalogId = null;

      await streamAskNovoAiResponse({
        aiApiEndpoint,
        token: jwtToken,
        streamRequest: streamRequestWithQuestion,
        abortSignal: streamAbortController.signal,
        onDelta: (chunk) => {
          if (!isActiveSubmission(submissionId)) return;
          if (!sharedAskNovoAiStreamRuntime.hasStreamedChunk) {
            sharedAskNovoAiStreamRuntime.hasStreamedChunk = true;
            clearLoadingStatusInterval();
            setSharedAskNovoAiStreamState({ streamStatusMessage: '' });
          }
          const nextValue = `${sharedAskNovoAiStreamRuntime.streamingAnswer}${chunk}`;
          sharedAskNovoAiStreamRuntime.streamingAnswer = nextValue;
          setSharedAskNovoAiStreamState({ streamingAnswer: nextValue });
        },
        onResult: (result: AskNovoAiStreamResultPayload) => {
          if (!isActiveSubmission(submissionId)) return;
          clearLoadingStatusInterval();
          const streamResponse = buildImmediateStreamResponse(result, lecturePageTitleById);
          sharedAskNovoAiStreamRuntime.streamResponse = streamResponse;
          dispatch(setAskNovoAiResponse(streamResponse));
        },
        onDone: (result) => {
          if (!isActiveSubmission(submissionId)) return;
          if (result.persistenceState === 'persistence_enqueue_failed') {
            return;
          }

          dispatch(getRecentQuestions(catalogId))
            .unwrap()
            .then((recentQuestions) => {
              if (!isActiveSubmission(submissionId) || !sharedAskNovoAiStreamRuntime.streamResponse?.questionText) {
                return;
              }

              const savedQuestion = recentQuestions.find(
                (recentQuestion) => recentQuestion.questionText === sharedAskNovoAiStreamRuntime.streamResponse?.questionText,
              );
              if (!savedQuestion || sharedAskNovoAiStreamRuntime.streamResponse.id) {
                return;
              }

              const streamResponse = {
                ...sharedAskNovoAiStreamRuntime.streamResponse,
                id: savedQuestion.id,
              };
              sharedAskNovoAiStreamRuntime.streamResponse = streamResponse;
              dispatch(setAskNovoAiResponse(streamResponse));
            })
            .catch(() => {});
        },
        onError: (error) => {
          if (!isActiveSubmission(submissionId)) return;
          clearLoadingStatusInterval();
          const streamResponse = {
            questionText,
            answer: sharedAskNovoAiStreamRuntime.streamingAnswer,
            responseCode: 500,
            liveEvents: [],
            referencedComponents: [],
            externalReferences: [],
          };
          sharedAskNovoAiStreamRuntime.streamResponse = streamResponse;
          dispatch(setAskNovoAiResponse(streamResponse));
          setVisibleStreamStatus(error.message, true);
        },
      });
    } catch (_error) {
      if (!isActiveSubmission(submissionId)) return;
      clearLoadingStatusInterval();
      const defaultErrorMessage = t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.ERROR_DEFAULT_MESSAGE();
      const networkErrorMessage = t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.NETWORK_ERROR_MESSAGE();
      const error = _error instanceof Error ? _error : null;
      const isControlledError = !!error && (
        error.message === defaultErrorMessage
        || error.message === networkErrorMessage
      );

      const streamResponse = {
        questionText,
        answer: isControlledError && error ? error.message : '',
        responseCode: isControlledError ? 400 : 500,
        liveEvents: [],
        referencedComponents: [],
        externalReferences: [],
      };
      sharedAskNovoAiStreamRuntime.streamResponse = streamResponse;
      dispatch(setAskNovoAiResponse(streamResponse));
      if (isControlledError && error) {
        setVisibleStreamStatus(error.message, true);
      }
    } finally {
      if (
        isActiveSubmission(submissionId)
        && sharedAbortController === streamAbortController
      ) {
        sharedAbortController = null;
      }
    }

    if (isProcessingFilesInProgress) {
      dispatch(getHelpOptionsSettings({ catalogId }));
    }
  }, [
    catalogId,
    dispatch,
    isActiveSubmission,
    isProcessingFilesInProgress,
    lecturePageTitleById,
    prefetchStreamSession,
    resetLocalStreamState,
    setVisibleStreamStatus,
    startLoadingStatusSequence,
    clearLoadingStatusInterval,
  ]);

  React.useEffect(() => {
    if (askNovoAi.isOpen && askNovoAi.drawerState === AskNovoAiDrawerState.INITIAL) {
      dispatch(getRecentQuestions(catalogId));
      prefetchStreamSession().catch(() => {
        sharedAskNovoAiStreamRuntime.streamSession = null;
        sharedAskNovoAiStreamRuntime.streamSessionCatalogId = null;
      });
    }
  }, [askNovoAi.drawerState, askNovoAi.isOpen, catalogId, dispatch, prefetchStreamSession]);

  React.useEffect(() => {
    const hasActiveStream = !!sharedAbortController && !!sharedAskNovoAiStreamState.activeQuestion;
    if (!hasActiveStream || sharedAskNovoAiStreamRuntime.streamResponse) return;

    if (!askNovoAi.isOpen) {
      dispatch(openNovoAIDialog());
    }

    if (askNovoAi.drawerState !== AskNovoAiDrawerState.LOADING) {
      dispatch(changeDrawerState(AskNovoAiDrawerState.LOADING));
    }
  }, [askNovoAi.drawerState, askNovoAi.isOpen, dispatch]);

  const resetStream = React.useCallback(() => {
    invalidateActiveSubmission();
    sharedAbortController?.abort();
    sharedAbortController = null;
    sharedAskNovoAiStreamRuntime.streamSession = null;
    sharedAskNovoAiStreamRuntime.streamSessionCatalogId = null;
    resetLocalStreamState();
    dispatch(resetAskNovoAiUiState());
    dispatch(openNovoAIDialog());
  }, [dispatch, invalidateActiveSubmission, resetLocalStreamState]);

  return {
    activeQuestion,
    handleCloseDialog,
    onSubmit,
    resetStream,
    streamStatusMessage,
    streamingAnswer,
  };
};

export default useAskNovoAiStream;
